import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Download, Loader2, Shield } from 'lucide-react';
import { sitesApi } from '@/api/client';
import { RoleGate } from '@/components/auth/RoleGate';
import { Role } from '@jeket-cloud/types/auth';
import { ReplaceUsbImageDialog } from '@/components/deployments/ReplaceUsbImageDialog';
import type { Site } from '@/types';

/**
 * The site's Boot Images card. Its actions are downloading the install image
 * (GET /sites/:id/boot-image, SiteOperator at the BFF) and replacing it
 * (Admin, gated inside), so below SiteOperator it offered only a 403 and is
 * not shown at all (JSS#970).
 */
export function BootImagesCard({ site }: { site: Pick<Site, 'id' | 'name' | 'bootMediaRotatedAt'> }) {
  return (
    <RoleGate role={Role.SiteOperator}>
      <div className="bg-surface border border-surface-elevated rounded-xl p-5" data-testid="boot-images-card">
        <h3 className="text-sm font-semibold text-silver-bright mb-4">Boot Images</h3>
        <BootImages site={site} />
      </div>
    </RoleGate>
  );
}

function BootImages({ site }: { site: Pick<Site, 'id' | 'name' | 'bootMediaRotatedAt'> }) {
  const queryClient = useQueryClient();
  const [replacing, setReplacing] = useState(false);
  // Downloaded through JSS with the signed-in session, then saved from memory.
  // The image's Image Factory URL carries the site's join token, so there is
  // no link to open or copy (JSS#877). The server builds the image on demand.
  const download = useMutation({
    mutationFn: async (arch: 'arm64' | 'amd64') => {
      const { blob, filename } = await sitesApi.downloadBootImage(site.id, arch);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    },
  });

  return (
    <div className="space-y-3">
      <p className="text-[10px] text-silver-muted">
        Minimal boot ISO — gets the machine into maintenance mode. Its extensions are added when the box receives its configuration.
        Any box booted from it joins this site, so keep it private.
      </p>
      {site.bootMediaRotatedAt && (
        <p className="text-[10px] text-silver-muted" data-testid="boot-media-rotated-at">
          Replaced {new Date(site.bootMediaRotatedAt).toLocaleString()}: images downloaded before then no longer add boxes.
        </p>
      )}
      {(['arm64', 'amd64'] as const).map((arch) => (
        <div key={arch} className="bg-surface-deep rounded-lg p-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-xs">
              <span className="font-medium text-silver-bright">{arch.toUpperCase()}</span>
              <span className="font-mono text-silver-muted">metal-{arch}.iso</span>
            </div>
            <button
              onClick={() => download.mutate(arch)}
              disabled={download.isPending}
              className="text-gold hover:text-gold-light transition-colors disabled:opacity-50"
              title="Download"
            >
              {download.isPending && download.variables === arch
                ? <Loader2 size={14} className="animate-spin" />
                : <Download size={14} />}
            </button>
          </div>
        </div>
      ))}
      {download.isPending && (
        <p className="text-[10px] text-silver-muted">Preparing the image — the first download can take a minute or two.</p>
      )}
      {download.isError && (
        <p className="text-xs text-status-error">Failed to download the boot image</p>
      )}
      {/* JSS#890: revoke a lost or leaked USB. Admin — it cuts off every copy. */}
      <RoleGate role={Role.Admin}>
        <button
          type="button"
          onClick={() => setReplacing(true)}
          className="flex items-center gap-1.5 text-xs text-silver-muted hover:text-silver-bright transition-colors"
          data-testid="replace-usb-open"
        >
          <Shield size={12} />
          Replace USB image…
        </button>
      </RoleGate>
      {replacing && (
        <ReplaceUsbImageDialog
          siteId={site.id}
          siteName={site.name}
          onClose={() => setReplacing(false)}
          onDownload={(arch) => download.mutate(arch)}
          downloading={download.isPending ? download.variables ?? null : null}
          onReplaced={() => {
            void queryClient.invalidateQueries({ queryKey: ['sites'] });
            void queryClient.invalidateQueries({ queryKey: ['site', site.id] });
            void queryClient.invalidateQueries({ queryKey: ['machines'] });
          }}
        />
      )}
    </div>
  );
}
